<script lang="ts" setup>
  import { twMerge } from 'tailwind-merge'
  import type { IRoute } from '@/utils/constants/trending-routes/types'
  import useIndexPageDataLayer from '@/composables/data-layers/useIndexPageDataLayer'
  import useSingleDestinationPageDataLayer from '@/composables/data-layers/useSingleDestinationPageDataLayer'

  const props = withDefaults(
    defineProps<{
      title?: string
      excludedDestinationCode?: string
      containerClass?: string
    }>(),
    {
      title: undefined,
      excludedDestinationCode: undefined,
      containerClass: undefined
    }
  )

  const containerClasses = computed(() => twMerge('mt-12 lg:mt-16 xl:mt-18', props.containerClass))

  const modules = import.meta.glob('@/utils/constants/trending-routes/routes/*-routes.ts') as Record<
    string,
    () => Promise<{ routes: IRoute[] }>
  >

  const { locale } = useI18n()

  const loadRoutesData = async (): Promise<IRoute[]> => {
    const modulePath = `/utils/constants/trending-routes/routes/${locale.value}-routes.ts`

    const loader = modules[modulePath]
    if (!loader) {
      console.error(`No trending route module found for locale: ${locale.value}`)
      return []
    }

    try {
      const module = await loader()

      return module.routes
    } catch (error) {
      console.error(`Failed to load trending routes for locale ${locale.value}:`, error)
      return []
    }
  }

  const routes = await loadRoutesData()
  const normalizedExcludedDestinationCode = computed(() => props.excludedDestinationCode?.trim().toUpperCase())
  const filteredRoutes = computed(() => {
    if (!normalizedExcludedDestinationCode.value) return routes

    return routes.filter((route) => route.destinationCode !== normalizedExcludedDestinationCode.value)
  })

  const { isDesktop } = useBTBreakpoints()
  const VISIBLE_ROUTES_COUNT = {
    MOBILE: 6,
    DESKTOP: 12
  }

  const routesPerLoad = computed(() => (isDesktop.value ? VISIBLE_ROUTES_COUNT.DESKTOP : VISIBLE_ROUTES_COUNT.MOBILE))

  const visibleRoutesCount = ref(0)
  const visibleRoutes = computed(() => filteredRoutes.value.slice(0, visibleRoutesCount.value))
  const canLoadMore = computed(() => visibleRoutesCount.value < filteredRoutes.value.length)

  const section = ref<HTMLElement | null>(null)
  const { pushTrendingRoutesSectionDataLayer } = useIndexPageDataLayer()

  const destinCode = inject<string>('destinCode', '')
  const { pushDiscoverOtherDestinationsImpressionDataLayer, pushSectionClickDataLayer } =
    useSingleDestinationPageDataLayer(destinCode)

  watch(
    routesPerLoad,
    (value) => {
      visibleRoutesCount.value = Math.max(visibleRoutesCount.value, value)
    },
    { immediate: true }
  )

  const loadMore = () => {
    visibleRoutesCount.value += routesPerLoad.value
  }

  function pushRouteImpressions(routesToTrack: IRoute[]) {
    routesToTrack.forEach((route) => pushDiscoverOtherDestinationsImpressionDataLayer(route.destinationCode))
  }

  let isSectionVisible = false

  onElementVisibleAtThreshold(section, () => {
    pushTrendingRoutesSectionDataLayer({
      event_category: 'section',
      event_action: 'impressions'
    })

    isSectionVisible = true
    pushRouteImpressions(visibleRoutes.value)
  })

  watch(visibleRoutes, (routes) => {
    if (!isSectionVisible) return

    pushRouteImpressions(routes)
  })

  const onRouteClick = async (route: IRoute) => {
    pushTrendingRoutesSectionDataLayer({
      event_category: 'card',
      event_action: 'click',
      event_label: route.anchor
    })

    if (destinCode) {
      pushSectionClickDataLayer({
        event_action: 'click',
        event_label: 'discover_other_destinations',
        destination_airport_code: route.destinationCode
      })
    }

    await navigateTo(route.url, {
      external: route.url.startsWith('http')
    })
  }
</script>

<template>
  <div
    v-if="filteredRoutes.length"
    ref="section"
    :class="containerClasses"
  >
    <h2
      v-if="title"
      class="mb-8 text-h2 md:mb-10 md:text-h3 lg:mb-8 lg:text-h2"
    >
      {{ title }}
    </h2>
    <div class="flex w-full flex-wrap gap-4 md:gap-6">
      <UiLinkButton
        v-for="route in visibleRoutes"
        :key="route.url"
        :url="route.url"
        :title="route.anchor"
        variant="secondaryOutline"
        @click.prevent="onRouteClick(route)"
      />

      <UiButton
        v-if="canLoadMore"
        variant="underline"
        :title="$t('showMore')"
        @click="loadMore"
      />
    </div>
  </div>
</template>
